import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { wholeContainerStyle, mainColumnStyle, HEADER_HEIGHT, SIDE_MENU_WIDTH } from '@/constants/layout'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { LoadingFull } from '@/components/molecules/loading-full'
import { RomStatusBox } from '@/components/organisms/room-manager/indicator/room-status-box'
import { RemainingRoomBox } from '@/components/organisms/room-manager/indicator/remaining-room-box'
import { AllRoomsOnFloor } from '@/components/organisms/room-manager/indicator/all-rooms-on-floor'
import { IndicatorInventoryType, RoomListType, RoomStatusType, TotalType } from '@/models/room-manager/response/guest-room-indicator'
import dayjs, { Dayjs } from 'dayjs'
import { fetchGuestRoomIndicator } from '@/apis/aipass'
import { useTranslation } from 'react-i18next'
import { useAutoReload } from '@/hooks/use-auto-reload'
import { DateField } from '@/components/molecules/date-field'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { AccountContext } from '@/contexts/account'
import { Select, Option } from '@/components/atoms/select'
import { SelectProps } from 'antd/lib/select'

export type RoomStatusFilter = Array<RoomStatusType>
export type RoomTypeFilter = Array<string>

export type DisplaySizeType = 'tiny' | 'small' | 'medium' | 'large'

export const RoomManagerIndicatorContext = createContext<{
  doReload?: () => void
  reloadEvent?: string
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>
  columnsPerRow?: number
}>({})

/**
 * 部屋表示サイズの選択肢
 * columnsは1行に表示する部屋の列数（フロアボックス除く）
 * デフォルトは15列
 */
const DISPLAY_SIZE_OPTIONS: { value: DisplaySizeType; label: string; columns: number }[] = [
  { value: 'tiny', label: '極小(15列)', columns: 15 },
  { value: 'small', label: '小(12列)', columns: 12 },
  { value: 'medium', label: '中(9列)', columns: 9 },
  { value: 'large', label: '大(6列)', columns: 6 },
]

export const RoomManagerIndicator: React.FC = () => {
  const query = require('query-string').parse(window.location.search)
  const { account } = useContext<any>(AccountContext)
  // ホテルごとに表示サイズの設定を保存
  const displaySizeStorageKey = `RoomIndicatorDisplaySize-${account?.hotel?.id || 'default'}`

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [reloadEvent, setReloadEvent] = useState<string>(dayjs().toISOString())
  const [roomStatusFilter, setRoomStatusFilter] = useState<RoomStatusFilter>()
  const [roomTypeFilter, setRoomTypeFilter] = useState<RoomTypeFilter>()
  const [roomEachFloor, setRoomEachFloor] = useState<RoomListType[]>([])
  const [total, setTotal] = useState<TotalType>()
  const [inventories, setInventories] = useState<IndicatorInventoryType[]>([])
  const [dateOf, setDateOf] = useState<Dayjs>(dayjs(query?.dateOf || undefined))
  // localStorageから保存された表示サイズを読み込み、なければデフォルト（'tiny' = 15列）を使用
  const [displaySize, setDisplaySize] = useState<DisplaySizeType>(() => {
    const saved = localStorage.getItem(displaySizeStorageKey) as DisplaySizeType
    return DISPLAY_SIZE_OPTIONS.some(o => o.value === saved) ? saved : 'tiny'
  })

  const history = useHistory()
  const { t } = useTranslation()
  const { setReloadCallback } = useAutoReload()

  /**
   * displaySizeから実際の列数を取得
   * 選択されたサイズに対応する列数がない場合はデフォルト15列を返す
   * この値はAllRoomsOnFloorコンポーネントに渡され、1行の部屋表示数として使用される
   */
  const columnsPerRow = useMemo(() => {
    const option = DISPLAY_SIZE_OPTIONS.find(opt => opt.value === displaySize)
    return option ? option.columns : 15
  }, [displaySize])

  /**
   * 表示サイズ変更時にlocalStorageに保存
   */
  const handleDisplaySizeChange: SelectProps['onChange'] = value => {
    // 単一選択なので配列の場合は最初の要素を使用
    const sizeValue = Array.isArray(value) ? value[0] : value
    // 有効な値かバリデーション
    if (!DISPLAY_SIZE_OPTIONS.some(o => o.value === sizeValue)) {
      return
    }
    const size = sizeValue as DisplaySizeType
    setDisplaySize(size)
    localStorage.setItem(displaySizeStorageKey, size)
  }

  const doReload = (viewDate?: Dayjs) => {
    initializeComponent(viewDate || dateOf)
    setReloadEvent((viewDate || dateOf).toISOString())
  }

  const initializeComponent = (viewDate: Dayjs) => {
    setIsLoading(true)
    fetchGuestRoomIndicator(viewDate)
      .then(res => {
        if (!res) {
          return
        }
        setRoomEachFloor(res.roomsEachFloor)
        setTotal(res.total)
        setInventories(res.inventories)
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    doReload()
    history.replace({
      search: new URLSearchParams({ dateOf: dateOf.format('YYYY-MM-DD') }).toString(),
    })
  }, [dateOf])

  useEffect(() => {
    /**
     * 60秒ごとの自動リロード設定
     * intervalプロセスからstateの参照ができないため、setter関数内で現在値を取得してから使用する
     */
    setReloadCallback(() => {
      let originSelectedDate
      setDateOf(d => {
        originSelectedDate = d
        return d
      })
      doReload(originSelectedDate)
    }, 60000)
  }, [])

  const isTodayView = useMemo(() => dateOf.isSameOrBefore(dayjs(), 'day'), [dateOf])

  return (
    <RoomManagerIndicatorContext.Provider
      value={{
        setIsLoading,
        doReload,
        reloadEvent,
        columnsPerRow,
      }}
    >
      <div css={wholeContainerStyle}>
        <SideMenu />
        <div className="mainColumn" css={mainColumnStyle}>
          <Header title={t('Room management')} />
          <div css={roomContainerStyle}>
            <div style={{ padding: 24, paddingTop: 6, height: '100%' }}>
              <div style={{ display: 'flex', marginBottom: 6, alignItems: 'center' }}>
                <img
                  style={isTodayView ? { visibility: 'hidden' } : { cursor: 'pointer' }}
                  src={require('@/static/images/arrow_left_gray.svg')}
                  onClick={() => {
                    if (isTodayView) {
                      return
                    }
                    setDateOf(dateOf.subtract(1, 'day'))
                  }}
                />
                <DateField
                  customCss={overWriteDateFieldStyle}
                  value={dateOf.format('YYYY-MM-DD')}
                  displayFormat={dateOf.isSame(dayjs(), 'day') ? '現在状況' : t('YYYY-MM-DD')}
                  onChange={date => date && setDateOf(dayjs(date.format('YYYY-MM-DD')))}
                  isOutsideRange={day => day.isBefore(moment(), 'day')}
                />
                <img
                  style={{ cursor: 'pointer' }}
                  src={require('@/static/images/arrow_right_gray.svg')}
                  onClick={() => setDateOf(dateOf.add(1, 'day'))}
                />

                {!dateOf.isSame(dayjs(), 'day') && (
                  <Button buttonType={ButtonType.NonStyle} fontSize={12} width={100} height={28} onClick={() => setDateOf(dayjs())}>
                    現在状況
                  </Button>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 'bold', fontSize: 12 }}>部屋サイズ：</span>
                  <Select
                    value={displaySize}
                    onChange={handleDisplaySizeChange}
                    customStyle={css({
                      paddingBottom: 0,
                      width: 150,
                    })}
                  >
                    {DISPLAY_SIZE_OPTIONS.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>

              <div css={mainWrapperStyle}>
                <div className="box-area">
                  <RomStatusBox setRoomStatusFilter={setRoomStatusFilter} total={total} />
                  <RemainingRoomBox setRoomTypeFilter={setRoomTypeFilter} inventories={inventories} />
                </div>

                <div className="room-area">
                  <AllRoomsOnFloor roomStatusFilter={roomStatusFilter} roomTypeFilter={roomTypeFilter} roomEachFloor={roomEachFloor} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <LoadingFull isLoading={isLoading} />
      </div>
    </RoomManagerIndicatorContext.Provider>
  )
}

const roomContainerStyle = css({
  position: 'relative',
  height: `calc(100% - ${HEADER_HEIGHT}px)`,
})

const mainWrapperStyle = css({
  minWidth: 1177,
  // 48px = padding space
  width: `calc(100% + 100vw - ${SIDE_MENU_WIDTH}px - 48px - 100%)`,
  background: '#ffffff',
  height: '100%',
  boxShadow: '0px 0px 6px #0000001A',
  borderRadius: 5,
  overflow: 'auto',
  display: 'flex',
  flexFlow: 'column',
  '.box-area': {
    display: 'flex',
  },
  '.room-area': {
    marginTop: 18,
    marginBottom: 2,
    flexGrow: 1,
  },
})

const overWriteDateFieldStyle = css({
  'div.SingleDatePicker': {
    '.SingleDatePickerInput': {
      width: '100%',
      border: 'none',
      backgroundColor: 'transparent',
      'div.SingleDatePicker_picker': {
        zIndex: 100,
      },
      '.SingleDatePickerInput_calendarIcon': { display: 'none' },
      'div.DateInput': {
        'input.DateInput_input': {
          padding: '5px 0',
          fontSize: 12,
          lineHeight: '24px',
          fontWeight: 'bold',
          color: 'rgba(0, 0, 0, 0.65)',
          letterSpacing: '0.8px',
        },
      },
    },
  },
})
